<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Eye, EyeOff } from "lucide-vue-next";
  import SearchInput from "@/components/episto-ui/search-input/SearchInput.vue";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { Button } from "@/components/ui/button";
  import CollapseExpandAllButton from "@/components/episto-ui/collapse-card/CollapseExpandAllButton.vue";
  import { useCAdsetsStore } from "@stores/cAdsets";
  import { useCCampaignsStore } from "@stores/cCampaigns";
  import { useCAdsetsFilters } from "@composables/cAdsetsFilters";
  import { useBudgetView } from "@composables/budget/useBudgetView";
  import { useBudgetSelection } from "@composables/budget/useBudgetSelection";

  const cAdsetsStore = useCAdsetsStore();
  const { cCampaignStatusFilter, cAdsetsStatusFilter, cAdsetsGenderFilter, cAdsetsAgeFilter, cAdsetsSearchQuery } =
    storeToRefs(cAdsetsStore);
  const { clearFiltersCAdsets } = cAdsetsStore;

  const { cCampaigns } = storeToRefs(useCCampaignsStore());

  const {
    updateCCampaignStatusFilter,
    updateCAdsetsStatusFilter,
    updateCAdsetsGenderFilter,
    updateCAdsetsSearchQuery,
  } = useCAdsetsFilters();

  const { showConstraints, collapsedElements, expandAll, collapseAll } = useBudgetView();
  const { selectedCount, clearSelection } = useBudgetSelection();

  const filterConfigs = computed(() => [
    {
      id: "ccampaign_status",
      label: "Sub-campaign status",
      type: "checkbox",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      selectedValues: cCampaignStatusFilter.value,
    },
    {
      id: "adset_status",
      label: "Adset status",
      type: "checkbox",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      selectedValues: cAdsetsStatusFilter.value,
    },
    {
      id: "adset_gender",
      label: "Gender",
      type: "checkbox",
      options: [
        { label: "All", value: "all" },
        { label: "Women", value: "women" },
        { label: "Men", value: "men" },
      ],
      selectedValues: cAdsetsGenderFilter.value,
    },
    {
      id: "adset_age",
      label: "Age range",
      type: "numberRange",
      value: { min: cAdsetsAgeFilter.value[0], max: cAdsetsAgeFilter.value[1] },
      minLimit: 0,
      maxLimit: 100,
    },
  ]);

  const activeFiltersCount = computed(
    () =>
      cCampaignStatusFilter.value.length +
      cAdsetsStatusFilter.value.length +
      cAdsetsGenderFilter.value.length +
      (cAdsetsAgeFilter.value[0] !== 0 || cAdsetsAgeFilter.value[1] !== 100 ? 1 : 0),
  );

  const filterToUpdater = {
    ccampaign_status: updateCCampaignStatusFilter,
    adset_status: updateCAdsetsStatusFilter,
    adset_gender: updateCAdsetsGenderFilter,
  };

  const filterToValueRef = {
    ccampaign_status: cCampaignStatusFilter,
    adset_status: cAdsetsStatusFilter,
    adset_gender: cAdsetsGenderFilter,
  };

  const handleFilterToggle = (filterId, value) => {
    const updater = filterToUpdater[filterId];
    const valueRef = filterToValueRef[filterId];
    if (!updater || !valueRef) return;
    const checkboxValue = !valueRef.value.includes(value);
    updater({ filterValue: value, checkboxValue });
  };

  const handleAgeRange = (_filterId, range) => {
    cAdsetsAgeFilter.value = [range.min, range.max];
  };

  const handleClearFilters = () => {
    clearFiltersCAdsets();
    clearSelection();
  };

  const allCollapsed = computed(
    () => cCampaigns.value.length > 0 && collapsedElements.value.length === cCampaigns.value.length,
  );

  const toggleAllRows = () => {
    if (allCollapsed.value) {
      expandAll();
    } else {
      collapseAll(cCampaigns.value.map((cc) => cc.group_token));
    }
  };
</script>

<template>
  <div class="flex flex-wrap items-center gap-2 p-4">
    <SearchInput
      :model-value="cAdsetsSearchQuery"
      placeholder="Search ad sets..."
      size="default"
      @update:model-value="updateCAdsetsSearchQuery" />

    <FilterDropdown
      :filters="filterConfigs"
      :active-filters-count="activeFiltersCount"
      @toggle="handleFilterToggle"
      @update:number-range="handleAgeRange"
      @clear="handleClearFilters" />

    <CollapseExpandAllButton
      :expanded="!allCollapsed"
      @toggle="toggleAllRows" />

    <Button
      variant="outline"
      :class="showConstraints ? 'bg-accent' : ''"
      @click="showConstraints = !showConstraints">
      <component
        :is="showConstraints ? EyeOff : Eye"
        class="h-4 w-4" />
      {{ showConstraints ? "Hide constraints" : "Show constraints" }}
    </Button>

    <div
      v-if="selectedCount > 0"
      class="ml-auto flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1 text-sm">
      <span>{{ selectedCount }} selected</span>
      <Button
        variant="ghost"
        size="sm"
        @click="clearSelection">
        Clear
      </Button>
    </div>
  </div>
</template>
